import { faRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ConfirmationModal, Flex, Text, TextButton } from '@modusign/design-system';
import { updateDocument } from '@modusign/openapi-client-external-document';
import { UpdateExternalDocumentDto } from '@modusign/openapi-client-external-document/generated/model';
import { FormEvent, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form-v7';
import { useMutation } from 'react-query';
import { useToggle } from 'react-use';

import noop from '@libs/noop';

import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import { ExternalDocument } from '../../../../types/ExternalDocument';
import BasicInformationEditor, { BasicInformationFormState } from '../../../BasicInformationEditor';

interface EditorProps {
  externalDocument: ExternalDocument;
  onIsDirtyChange: (isDirty: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function Editor({
  externalDocument,
  onIsDirtyChange,
  onSuccess,
  onCancel,
}: EditorProps) {
  const { createErrorHandler } = useOpenErrorModal();
  const workspaceId = useWorkspaceId();

  const [isResetModalOpen, toggleResetModal] = useToggle(false);

  const { mutateAsync } = useMutation(
    (params: UpdateExternalDocumentDto) => updateDocument(workspaceId, externalDocument.id, params),
    {
      onError: createErrorHandler({ title: '외부 문서 수정 실패' }),
      onSuccess,
    }
  );

  const formMethods = useForm<BasicInformationFormState>({
    mode: 'onChange',
    defaultValues: {
      title: externalDocument.title,
      type: externalDocument.type,
      status: externalDocument.status || '',
      personInCharge: {
        name: externalDocument.personInCharge?.name || '',
        contact: {
          email: externalDocument.personInCharge?.email || '',
          phone: externalDocument.personInCharge?.phone || '',
        },
      },
      originalLocation: externalDocument.originalLocation || undefined,
      memo: externalDocument.memo || undefined,
      dateOfCompletion: externalDocument.dateOfCompletion,
      documentPeriod: externalDocument.documentPeriod || undefined,
      renewal: externalDocument.renewal
        ? {
            periodInYears: externalDocument.renewal.period.value,
            noticeBeforeExpirationInDays: externalDocument.renewal.noticeBeforeExpiration?.value,
          }
        : undefined,
      renewalRadio: externalDocument.renewal ? '1' : '0',
    },
  });

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    reset,
    trigger,
  } = formMethods;

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  const submitHandler = handleSubmit(
    (data) =>
      mutateAsync({
        personInCharge: {
          name: data.personInCharge.name,
          email: data.personInCharge.contact?.email || null,
          phone: data.personInCharge.contact?.phone || null,
        },
        title: data.title,
        dateOfCompletion: data.dateOfCompletion || null,
        documentPeriod: data.documentPeriod.from
          ? { from: data.documentPeriod.from, to: data.documentPeriod.to || null }
          : null,
        memo: data.memo || null,
        originalLocation: data.originalLocation || null,
        renewal: data.renewal
          ? {
              noticeBeforeExpiration:
                data.renewal.noticeBeforeExpirationInDays === -1
                  ? null
                  : {
                      unit: 'day',
                      value: data.renewal.noticeBeforeExpirationInDays,
                    },
              period: {
                unit: 'year',
                value: data.renewal.periodInYears,
              },
            }
          : null,
        status: data.status || undefined,
        type: data.type || undefined,
      }).catch(noop),
    () => trigger()
  );

  return (
    <>
      <form
        onReset={(e: FormEvent) => {
          e.preventDefault();
          toggleResetModal();
        }}
        onSubmit={submitHandler}
      >
        <FormProvider {...formMethods}>
          <BasicInformationEditor />
        </FormProvider>
        <Flex
          alignItems="center"
          flexDirection="row-reverse"
          justifyContent="space-between"
          mb={[10, null, 0]}
          mt={9}
        >
          <div>
            <Button disabled={isSubmitting} type="button" onClick={onCancel}>
              취소
            </Button>
            <Button isLoading={isSubmitting} ml={3} type="submit" variant="primary">
              저장하기
            </Button>
          </div>
          <TextButton disabled={isSubmitting} type="reset">
            <FontAwesomeIcon icon={faRotateLeft} /> 초기화
          </TextButton>
        </Flex>
      </form>
      <ConfirmationModal
        confirmButtonLabel="초기화하기"
        isOpen={isResetModalOpen}
        maxWidth="xxs"
        title="정보 초기화"
        onClose={toggleResetModal}
        onConfirm={() => {
          reset({
            category: undefined,
            dateOfCompletion: undefined,
            documentPeriod: undefined,
            memo: '',
            originalLocation: '',
            personInCharge: { contact: { email: '', phone: '' }, name: '' },
            renewal: undefined,
            status: undefined,
            title: '',
            type: undefined,
          });
          toggleResetModal(false);
        }}
      >
        <Text textAlign="center">입력한 내용을 모두 삭제할까요?</Text>
      </ConfirmationModal>
    </>
  );
}
